import React, { useState } from 'react';
import { Casa } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import {
  Check, Copy, Crown, Edit2, Link2, Link2Off, LogOut, Plus, RefreshCw, Share2, Trash2,
  Star, UserCheck, UserMinus, UserX, X,
} from 'lucide-react';
import { MensajeError, ModalBase, botonIcono, claseInput } from './ui';
import { NOMBRE_APP, URL_PUBLICA } from '../../config/marca';
import { copiarTexto, compartirTexto, puedeCompartir } from '../../utils/plataforma';
import { colorDe } from '../../utils/tareas';

// URL_PUBLICA y no window.location: dentro de la app de iOS el origen es capacitor://localhost
export const enlaceInvitacionCasa = (token: string) => `${URL_PUBLICA}/tareas/unirse/${token}`;

/**
 * Quién vive en la casa y con qué rol. Igual que los participantes de A medias:
 * - Organizador: gestiona personas, la invitación y los roles.
 * - Miembro: ve la casa, apunta y marca tareas, y puede salir.
 * Quien ya tiene tareas o historial no se borra: se desactiva.
 */
export const ModalPersonasCasa: React.FC<{ casa: Casa; onClose: () => void; onSalir: () => void }> =
  ({ casa, onClose, onSalir }) => {
  const {
    tareas, hechas, anadirParticipante, actualizarParticipante, eliminarParticipante,
    abrirInvitacion, cerrarInvitacion, cambiarOrganizador, quitarMiembro, salirDeCasa,
  } = useTareas();
  const { user } = useAuth();
  const confirmar = useConfirmar();

  const [nuevo, setNuevo] = useState('');
  const [editando, setEditando] = useState<{ id: string; nombre: string } | null>(null);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const uid = user?.uid ?? '';
  const esOrganizador = casa.organizadoresUids.includes(uid);
  const soyUnicoOrganizador = esOrganizador && casa.organizadoresUids.length === 1;
  const esMiembro = (u?: string) => !!u && casa.miembrosUids.includes(u);
  const conHistorial = (pid: string) =>
    tareas.some(t => t.casaId === casa.id && t.asignadoA === pid)
    || hechas.some(h => h.casaId === casa.id && h.participanteId === pid);

  const existe = (nombre: string, salvo?: string) => casa.participantes.some(p =>
    p.id !== salvo && p.nombre.trim().toLowerCase() === nombre.trim().toLowerCase());

  const ejecutar = async (fn: () => Promise<unknown>) => {
    setError('');
    setOcupado(true);
    try { await fn(); } catch (err) {
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
    } finally { setOcupado(false); }
  };

  const anadir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nuevo.trim()) return;
    if (existe(nuevo)) { setError(`Ya hay alguien llamado «${nuevo.trim()}».`); return; }
    await ejecutar(async () => { await anadirParticipante(casa.id, nuevo); setNuevo(''); });
  };

  const guardarNombre = async () => {
    if (!editando) return;
    if (!editando.nombre.trim()) { setError('El nombre no puede quedar vacío.'); return; }
    if (existe(editando.nombre, editando.id)) { setError(`Ya hay alguien llamado «${editando.nombre.trim()}».`); return; }
    await ejecutar(async () => {
      await actualizarParticipante(casa.id, editando.id, { nombre: editando.nombre });
      setEditando(null);
    });
  };

  const copiar = async () => {
    if (!casa.invitacionToken) return;
    try {
      await copiarTexto(enlaceInvitacionCasa(casa.invitacionToken));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setError('No se pudo copiar. Mantén pulsado el enlace para copiarlo a mano.');
    }
  };

  const compartir = async () => {
    if (!casa.invitacionToken) return;
    try {
      await compartirTexto({
        titulo: casa.nombre,
        texto: `Únete a «${casa.nombre}» en ${NOMBRE_APP} para repartir las tareas de casa`,
        url: enlaceInvitacionCasa(casa.invitacionToken),
      });
    } catch {
      setError('No se pudo abrir el menú de compartir. Copia el enlace y pégalo donde quieras.');
    }
  };

  const salir = async () => {
    if (!(await confirmar({
      titulo: `¿Salir de «${casa.nombre}»?`,
      mensaje: 'Dejarás de ver sus tareas; lo que hiciste se queda en su historial.',
      accion: 'Salir',
    }))) return;
    await ejecutar(async () => {
      if (esOrganizador) await quitarMiembro(casa, uid);
      else await salirDeCasa(casa);
      onSalir();
    });
  };

  return (
    <ModalBase titulo={`Quién vive en ${casa.nombre}`} onClose={onClose}>
      <div className="p-5 space-y-5">
        <MensajeError texto={error} />

        {/* ── Invitación ───────────────────────────────────────────────── */}
        {esOrganizador && (
          <section className="rounded-xl border border-lumbre-200 dark:border-lumbre-800 bg-lumbre-50/60 dark:bg-lumbre-900/20 p-3 space-y-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Link2 className="w-4 h-4 text-lumbre-600" /> Invitar con un enlace
            </p>
            {casa.invitacionToken ? (
              <>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Quien lo abra podrá unirse, ver las tareas de la casa y marcarlas. Si no usa la app, se creará una cuenta.
                </p>
                <div className="flex gap-2">
                  <input readOnly value={enlaceInvitacionCasa(casa.invitacionToken)} onFocus={e => e.target.select()}
                    className={`${claseInput} text-xs`} aria-label="Enlace de invitación" />
                  <button onClick={copiar} title="Copiar enlace"
                    className="shrink-0 inline-flex items-center gap-1 px-3 rounded-lg bg-lumbre-600 hover:bg-lumbre-700 text-white text-xs font-semibold">
                    {copiado ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar</>}
                  </button>
                  {puedeCompartir() && (
                    <button onClick={compartir} title="Compartir" className={botonIcono}><Share2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <button disabled={ocupado} onClick={() => ejecutar(() => abrirInvitacion(casa))}
                    className="inline-flex items-center gap-1 font-semibold text-gray-600 dark:text-gray-400 hover:underline"
                    title="El enlace actual dejará de funcionar">
                    <RefreshCw className="w-3.5 h-3.5" /> Cambiar enlace
                  </button>
                  <button disabled={ocupado} onClick={() => ejecutar(() => cerrarInvitacion(casa))}
                    className="inline-flex items-center gap-1 font-semibold text-red-600 dark:text-red-400 hover:underline">
                    <Link2Off className="w-3.5 h-3.5" /> Cerrar invitación
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Crea un enlace y pásalo por WhatsApp o como quieras. Podrás cerrarlo cuando estéis todos.
                </p>
                <button disabled={ocupado} onClick={() => ejecutar(() => abrirInvitacion(casa))}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-lumbre-600 hover:bg-lumbre-700
                             text-white text-sm font-semibold transition disabled:opacity-60">
                  <Link2 className="w-4 h-4" /> Crear enlace de invitación
                </button>
              </>
            )}
          </section>
        )}

        {/* ── Lista ────────────────────────────────────────────────────── */}
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
          {casa.participantes.map(p => {
            const esYo = !!p.uid && p.uid === uid;
            const miembro = esMiembro(p.uid);
            const organiza = miembro && casa.organizadoresUids.includes(p.uid!);
            const estado = !p.uid ? 'Solo nombre' : miembro ? (organiza ? 'Organiza' : 'Miembro') : 'Ya no está en la casa';
            return (
              <li key={p.id} className={`flex items-center gap-1 px-3 py-2 ${p.activo ? '' : 'opacity-60'}`}>
                {editando?.id === p.id ? (
                  <>
                    <input type="text" value={editando.nombre} autoFocus
                      onChange={e => setEditando({ id: p.id, nombre: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); guardarNombre(); }
                        if (e.key === 'Escape') setEditando(null);
                      }}
                      className={claseInput} />
                    <button onClick={guardarNombre} title="Guardar" className={botonIcono}><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditando(null)} title="Cancelar" className={botonIcono}><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className={`w-3 h-3 rounded-full shrink-0 mr-2 ${colorDe(casa, p.id).punto}`} aria-hidden />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {p.nombre}{esYo && <span className="font-normal text-gray-500"> (tú)</span>}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        {organiza && <Crown className="w-3 h-3 text-amber-500" />}
                        {estado}{!p.activo && ' · Desactivado'}{p.peque && ' · ⭐ Peque'}
                      </p>
                    </div>

                    {esOrganizador && (
                      <>
                        {miembro && !esYo && (
                          <button disabled={ocupado}
                            onClick={() => ejecutar(() => cambiarOrganizador(casa, p.uid!, !organiza))}
                            title={organiza ? 'Dejar de organizar' : 'Hacer organizador: podrá gestionar la casa y su gente'}
                            className={`${botonIcono} ${organiza ? '!text-amber-500' : ''}`}>
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                        {esYo && organiza && !soyUnicoOrganizador && (
                          <button disabled={ocupado} onClick={() => ejecutar(() => cambiarOrganizador(casa, uid, false))}
                            title="Dejar de organizar" className={`${botonIcono} !text-amber-500`}>
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                        {!esYo && (
                          <button disabled={ocupado} aria-pressed={!!p.peque}
                            onClick={() => ejecutar(() => actualizarParticipante(casa.id, p.id, { peque: !p.peque }))}
                            title={p.peque ? 'Ya no es un peque' : 'Es un peque: gana estrellas por sus tareas'}
                            className={`${botonIcono} ${p.peque ? '!text-amber-500' : ''}`}>
                            <Star className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setEditando({ id: p.id, nombre: p.nombre })} title="Cambiar nombre"
                          className={botonIcono}><Edit2 className="w-4 h-4" /></button>
                        {miembro && !esYo && (
                          <button disabled={ocupado}
                            onClick={async () => (await confirmar({
                                titulo: `¿Quitar a ${p.nombre} de la casa?`,
                                mensaje: 'Dejará de ver sus tareas; lo que hizo se queda.',
                                accion: 'Quitar',
                              })) && ejecutar(() => quitarMiembro(casa, p.uid!))}
                            title="Quitar de la casa" className={botonIcono}><UserMinus className="w-4 h-4" /></button>
                        )}
                        {!esYo && !miembro && (conHistorial(p.id) ? (
                          <button disabled={ocupado}
                            onClick={() => ejecutar(() => actualizarParticipante(casa.id, p.id, { activo: !p.activo }))}
                            title={p.activo ? 'Desactivar: no saldrá al asignar tareas nuevas' : 'Volver a activar'}
                            className={botonIcono}>
                            {p.activo ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                        ) : (
                          <button disabled={ocupado}
                            onClick={async () => (await confirmar({ titulo: `¿Quitar a ${p.nombre} de la casa?`, accion: 'Quitar' }))
                              && ejecutar(() => eliminarParticipante(casa.id, p.id))}
                            title="Quitar de la casa"
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>

        {esOrganizador && (
          <form onSubmit={anadir} className="flex gap-2">
            <input type="text" value={nuevo} onChange={e => setNuevo(e.target.value)}
              placeholder="Añadir a alguien solo por su nombre" className={claseInput} />
            <button type="submit" disabled={!nuevo.trim() || ocupado}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-lumbre-600 hover:bg-lumbre-700
                         text-white text-sm font-semibold transition disabled:opacity-50">
              <Plus className="w-4 h-4" /> Añadir
            </button>
          </form>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {esOrganizador
              ? 'La corona nombra organizadores: gestionan la casa, su gente y el enlace. La estrella marca a los peques: ganan estrellas por sus tareas.'
              : 'Quien organiza gestiona la casa; todos podéis crear y marcar tareas.'}
          </p>
          {esMiembro(uid) && !soyUnicoOrganizador && (
            <button onClick={salir} disabled={ocupado}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline">
              <LogOut className="w-3.5 h-3.5" /> Salir de la casa
            </button>
          )}
        </div>
      </div>
    </ModalBase>
  );
};
